import React, { useRef } from 'react';

import {
  GlobalCompanyTabName,
  GlobalCompanyTabRoleUID,
  GlobalCompanyTabUUID
} from '../../../globalCompanyTabsTypes';

import {
  useUpdateGlobalCompanyTabForm,
  UpdateGlobalCompanyTabFormData
} from './hooks/useUpdateGlobalCompanyTabForm';

import { useCurrentUser } from '../../../../../auth/hooks/useAuth';
import { useTranslate } from '../../../../../common/hooks/useTranslate';

import { RolesMultiSelectField } from '../../../../roles/helpers/RolesMultiSelectField';

import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { CheckPermissions } from '../../../../../helpers/CheckPermissions';
import { Form } from '../../../../../helpers/Form';
import { InputField } from '../../../../../helpers/FormFields/InputField';
import { SimpleModalButtons } from '../../../../../helpers/modals/SimpleModal/components/SimpleModalButtons';

import { TeamsPermissions } from '../../../../teams/teamsConstants';

import { formsFields, words } from '../../../../../locales/keys';

interface UpdateGlobalCompanyTabFormProps {
  form: string;
  globalCompanyTabUuid: GlobalCompanyTabUUID;
  name: GlobalCompanyTabName;
  roleIds: GlobalCompanyTabRoleUID[];
  hideModal: () => void;
}

function UpdateGlobalCompanyTabForm({
  form,
  globalCompanyTabUuid,
  name,
  roleIds,
  hideModal
}: UpdateGlobalCompanyTabFormProps) {
  const { t } = useTranslate();
  const submitButtonRef = useRef<HTMLButtonElement | null>(null);
  const currentUser = useCurrentUser();

  const {
    control,
    errors,
    registerName,
    handleSubmit,
    updateGlobalCompanyTabLoading,
    updateGlobalCompanyTabErrorMessage
  } = useUpdateGlobalCompanyTabForm({
    globalCompanyTabUuid,
    name,
    roleIds,
    hideModal
  });

  return (
    <>
      <div className="flex-1 overflow-y-auto px-2 text-sm">
        <Form id={form} className="p-4 space-y-4">
          {updateGlobalCompanyTabErrorMessage && (
            <AlertMessage message={updateGlobalCompanyTabErrorMessage} />
          )}

          <InputField
            error={
              errors.name ? errors.name.message || t(words.required) : null
            }
            disabled={updateGlobalCompanyTabLoading}
            inputWrapperClassName="mt-1 relative"
            i18nLabel={formsFields.name}
            labelClassName="block text-sm font-medium text-gray-700 dark:text-gray-300"
            name={registerName.name}
            onChange={registerName.onChange}
            ref={registerName.ref}
            required
          />

          <CheckPermissions
            action={
              TeamsPermissions.READ_UPDATE_GLOBAL_COMPANY_TAB_FORM_ACCESS_FIELD
            }
          >
            <RolesMultiSelectField<UpdateGlobalCompanyTabFormData>
              control={control}
              disabled={
                updateGlobalCompanyTabLoading ||
                !currentUser.hasPermissions(
                  TeamsPermissions.CHANGE_UPDATE_GLOBAL_COMPANY_TAB_FORM_ACCESS_FIELD
                )
              }
              name="roleIds"
              i18nLabel={formsFields.access}
              i18nPlaceholder={formsFields.access}
              labelClassName="block text-sm font-medium text-gray-700 dark:text-gray-300"
              inputWrapperClassName="mt-1 relative"
            />
          </CheckPermissions>
        </Form>
      </div>

      <SimpleModalButtons
        handleSubmit={handleSubmit}
        hideModal={hideModal}
        i18nCancelText={words.cancel}
        i18nSubmitText={words.save}
        isLoading={updateGlobalCompanyTabLoading}
        ref={submitButtonRef}
        submitButtonColor="blue"
      />
    </>
  );
}

export default UpdateGlobalCompanyTabForm;
